import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Dialog,
  DialogContent,
  Tab,
  Tabs,
  Paper,
  Table,
  TableContainer,
  TableBody,
  TableRow,
  TableCell,
  Typography,
} from '@material-ui/core';

import { makeStyles } from '@material-ui/core/styles';

import {
  Policy as PolicyIcon,
} from '@material-ui/icons';

import BaseButton from '../common/BaseButton';
import StatusIcon from '../common/StatusIcon';
import TabPanel from '../common/TabPanel';
import MatchTooltip from '../common/MatchTooltip';

import {
  selectGuest,
  selectReservation,
  selectRiskMatch,
  getGuestById,
  getRiskVerificationByGuestId,
  createRiskVerificationSession,
} from '../../store/widgetSlice';

import {
  setMessage,
} from '../../store/authSlice';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    maxWidth: '100%',
  },
  table: {
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(4),
    width: `calc(100% - ${theme.spacing(8)}px)`,
  },
  row: {
    justifyContent: 'space-between',
  },
  cell: {
    padding: 0,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),

    'tr:first-child &': {
      borderTop: '1px solid rgba(224, 224, 224, 1)',
    },

    'tr:last-child &': {
      borderBottom: 0,
    },
  },
  panel: {
    backgroundColor: theme.palette.background.default,
    minWidth: 550,
  },
  dialog: {
    padding: 0,
  },
  heading: {
    fontSize: 14,
    fontWeight: 600,
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    alignSelf: 'top',
  },
  cardHeading: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  name: {
    fontSize: 18,
    fontWeight: 600,
    textAlign: 'center',
  },
  equalHeight: {
    height: '100%',
  },
  ratingBox: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-between',
  },
  numberOfReviews: {
    fontSize: 14,
    fontWeight: 600,
    padding: 0,
  },
  ratingBars: {
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(4),
    paddingBottom: theme.spacing(1),
  },
  tableIcon: {
    verticalAlign: 'middle',
  },
}));

export default function VerifyModal({
  hideDefaultButton,
  externalOpen,
  externalHandleClose,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const guest = useSelector(selectGuest);
  const reservation = useSelector(selectReservation);
  const session = useSelector(selectRiskMatch);

  const [open, setOpen] = React.useState(false);
  const [currentTab, setCurrentTab] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (open && guest && guest.id) {
      dispatch(getRiskVerificationByGuestId({ guestId: guest.id }))
        .unwrap()
        .then(() => {
          dispatch(setMessage({ message: 'Match information retrieved', severity: 'success' }));
        })
        .catch(() => {
          if (reservation) {
            dispatch(setMessage({ message: 'Retrieving match information. Please wait...', severity: 'info' }));
            dispatch(createRiskVerificationSession({
              companyCode: reservation.companyCode,
              reservationCode: reservation.reservationCode,
            }))
              .unwrap()
              .then(() => {
                dispatch(getGuestById({ guestId: guest.id }));
                dispatch(setMessage({ message: 'Match information found', severity: 'success' }));
              })
              .catch(() => {
                dispatch(setMessage({ message: 'Match information not found', severity: 'error' }));
              });
          }
        });
    }
  }, [dispatch, guest, reservation, open]);

  useEffect(() => {
    setOpen(externalOpen);
  }, [externalOpen]);

  return (
    <div>
      {
        !hideDefaultButton
        && (
          <BaseButton
            IconComponent={PolicyIcon}
            onClick={handleClickOpen}
            fullWidth
          >
            Risk Score
          </BaseButton>
        )
      }
      <Dialog
        className={classes.dialog}
        open={open}
        onClose={externalHandleClose || handleClose}
        maxWidth="sm"
      >
        <Paper>
          <Tabs
            value={currentTab}
            indicatorColor="primary"
            textColor="primary"
            onChange={handleTabChange}
            variant="fullWidth"
          >
            <Tab label="Risk Status" />
          </Tabs>
        </Paper>
        <DialogContent className={classes.dialog}>
          <TabPanel className={classes.panel} value={currentTab} index={0}>
            {session && session.identity && session.identity.status
              && (
              <div className={classes.name}>
                <StatusIcon
                  className={classes.tableIcon}
                  status={session && session.identity && session.identity.status}
                  style={{ fontSize: 80 }}
                />
                <p>
                  <strong>Risk Level: </strong>
                  {session && session.identity && session.identity.valid}
                </p>
              </div>
              )}
            <TableContainer className={classes.container} component={Paper}>
              <Typography className={classes.heading} component="h3">Identity</Typography>
              <Table className={classes.table}>
                <TableBody>
                  <TableRow className={classes.row}>
                    <TableCell className={classes.cell} align="left">Identity Risk Score</TableCell>
                    <TableCell className={classes.cell} align="right">
                      {session && session.identity && session.identity.match}
                    </TableCell>
                    <TableCell className={classes.cell} align="right">
                      <StatusIcon
                        className={classes.tableIcon}
                        status={session && session.identity && session.identity.status}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow className={classes.row}>
                    <TableCell className={classes.cell} align="left">Identity Network Score</TableCell>
                    <TableCell className={classes.cell} align="right">
                      {(session && session.identity && (session.identity.identityNetworkScore * 100).toFixed(2)) || 0}
                      %
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <TableContainer className={classes.container} component={Paper}>
              <Typography className={classes.heading} component="h3">Address</Typography>
              <Table className={classes.table}>
                <TableBody>
                  <TableRow className={classes.row}>
                    <TableCell className={classes.cell} align="left">Address To Name</TableCell>
                    <TableCell className={classes.cell} align="right">
                      {session && session.address && session.address.match}
                    </TableCell>
                    <TableCell className={classes.cell} align="right">
                      <MatchTooltip
                        guest={session && session.address && session.address.guest}
                        match={session && session.address && session.address.match}
                      >
                        <StatusIcon
                          className={classes.tableIcon}
                          status={session && session.address && session.address.status}
                        />
                      </MatchTooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow className={classes.row}>
                    <TableCell className={classes.cell} align="left">First Used</TableCell>
                    <TableCell className={classes.cell} align="right">
                      {(session && session.address
                        && (session.address.firstSeenDays && `${session.address.firstSeenDays} days ago`))
                        || 'N/A'}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <TableContainer className={classes.container} component={Paper}>
              <Typography className={classes.heading} component="h3">Email</Typography>
              <Table className={classes.table}>
                <TableBody>
                  <TableRow className={classes.row}>
                    <TableCell className={classes.cell} align="left">Email To Name</TableCell>
                    <TableCell className={classes.cell} align="right">
                      {session && session.phone && session.email.match}
                    </TableCell>
                    <TableCell className={classes.cell} align="right">
                      <MatchTooltip
                        guest={session && session.email && session.email.guest}
                        match={session && session.email && session.email.match}
                      >
                        <StatusIcon
                          className={classes.tableIcon}
                          status={session && session.email && session.email.status}
                        />
                      </MatchTooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow className={classes.row}>
                    <TableCell className={classes.cell} align="left">Valid Email</TableCell>
                    <TableCell className={classes.cell} align="right">
                      {session && (session.email && session.email.valid ? 'Yes' : 'No')}
                    </TableCell>
                    <TableCell className={classes.cell} align="right">
                      <StatusIcon
                        className={classes.tableIcon}
                        status={session && session.email && (session.email.valid ? 3 : 1)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow className={classes.row}>
                    <TableCell className={classes.cell} align="left">Created</TableCell>
                    <TableCell className={classes.cell} align="right">
                      {(session && session.address
                        && (session.email.firstSeenDays && `${session.email.firstSeenDays} days ago`))
                        || 'N/A'}
                    </TableCell>
                  </TableRow>
                  <TableRow className={classes.row}>
                    <TableCell className={classes.cell} align="left">Domain Creation Date</TableCell>
                    <TableCell className={classes.cell} align="right">
                      {session && session.phone && session.email.domainCreationDate}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <TableContainer className={classes.container} component={Paper}>
              <Typography className={classes.heading} component="h3">Phone</Typography>
              <Table className={classes.table}>
                <TableBody>
                  <TableRow className={classes.row}>
                    <TableCell className={classes.cell} align="left">Phone To Name</TableCell>
                    <TableCell className={classes.cell} align="right">
                      {session && session.phone && session.phone.match}
                    </TableCell>
                    <TableCell className={classes.cell} align="right">
                      <MatchTooltip
                        guest={session && session.phone && session.phone.guest}
                        match={session && session.phone && session.phone.match}
                      >
                        <StatusIcon
                          className={classes.tableIcon}
                          status={session && session.phone && session.phone.status}
                        />
                      </MatchTooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow className={classes.row}>
                    <TableCell className={classes.cell} align="left">Valid Phone</TableCell>
                    <TableCell className={classes.cell} align="right">
                      {session && (session.phone && session.phone.valid ? 'Yes' : 'No')}
                    </TableCell>
                    <TableCell className={classes.cell} align="right">
                      <StatusIcon
                        className={classes.tableIcon}
                        status={session && session.phone && (session.phone.valid ? 3 : 1)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow className={classes.row}>
                    <TableCell className={classes.cell} align="left">Phone Line Type</TableCell>
                    <TableCell className={classes.cell} align="right">
                      {session && session.phone && session.phone.phoneLineType}
                    </TableCell>
                  </TableRow>
                  <TableRow className={classes.row}>
                    <TableCell className={classes.cell} align="left">Phone Carrier</TableCell>
                    <TableCell className={classes.cell} align="right">
                      {session && session.phone && session.phone.phoneCarrier}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </DialogContent>
      </Dialog>
    </div>
  );
}
